import { Icf } from "./assessment";
import { Sided } from "./sides";
import { between, roundToPrecision } from "./utils/utils";

export interface Score {
  /**
   * @returns Percentage between 0 and 1.
   */
  getFulfillment(): number;

  /**
   * @returns Percentage between 0 and 1.
   */
  getImpairement(): number;

  getIcfLevel(): Icf;
}

export class ScorePercent implements Score {
  /**
   * Use a static constructor method instead, like fromFulfillment().
   */
  private constructor(
    private fulfillment: number
  ) {
    this.fulfillment = roundToPrecision(this.fulfillment, 2)
  }

  /**
   * @param rawPercent Values below 0 or above 1 will be capped.
   */
  static fromFulfillment(rawPercent: number) {
    const fulfillment = between(0, rawPercent, 1);
    return new ScorePercent(fulfillment);
  }

  /**
   * @param rawPercent Values below 0 or above 1 will be capped.
   */
  static fromImpairement(rawPercent: number) {
    return this.fromFulfillment(1 - rawPercent)
  }

  getFulfillment(): number {
    return this.fulfillment
  }

  getImpairement(): number {
    return 1 - this.getFulfillment();
  }

  getIcfLevel(): Icf {
    return ScoreIcf.fromFulfillment(this.fulfillment).getIcfLevel();
  }
}

export class ScoreIcf implements Score {
  /**
   * Use a static constructor method instead, like fromFulfillment().
   */
  private constructor(
    private icfLevel: Icf
  ) { }

  static fromIcfLevel(level: Icf): ScoreIcf {
    return new ScoreIcf(level)
  }

  static fromImpairement(impairement: number): ScoreIcf {
    let level = Icf.Level4;

    if (impairement < 5) {
      level = Icf.Level0;
    } else if (impairement < 25) {
      level = Icf.Level1
    } else if (impairement < 50) {
      level = Icf.Level2
    } else if (impairement <= 95) {
      level = Icf.Level3
    }

    return new ScoreIcf(level)
  }

  static fromFulfillment(fulfillment: number): ScoreIcf {
    return this.fromImpairement(1 - fulfillment);
  }

  getFulfillment(): number {
    return 1 - this.getImpairement();
  }

  getImpairement(): number {
    switch (this.icfLevel) {
      case 0: return 0;
      case 1: return 0.15;
      case 2: return 0.35;
      case 3: return 0.70;
      default: return 1;
    }
  }

  getIcfLevel(): Icf {
    return this.icfLevel;
  }
}


export function getWorseSide(score: Sided<ScorePercent>): ScorePercent {
  const fulfillment = Math.min(score.left.getFulfillment(), score.right.getFulfillment());
  return ScorePercent.fromFulfillment(fulfillment);
}
